import React from "react";
import Layout from "../components/ui/Layout";
import { motion } from "framer-motion";
import { FaHammer, FaUsers, FaStar } from "react-icons/fa";
import { RiPlantLine, RiHeartLine, RiLightbulbLine } from "react-icons/ri";

export default function AboutUs() {
  return (
    <section className="w-full z-10">
      <Layout>
        {/* Hero Section */}
        <div className="relative mt-32 mb-24">
          <div className="absolute inset-0"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col md:flex-row gap-12 items-center"
            >
              <div className="w-full md:w-1/2 space-y-6">
                <h2 className="text-xl font-medium text-[#406580]">Chi Siamo</h2>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                  Benvenuti in Marrocco ART
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Marrocco ART è un brand innovativo, simbolo di esperienza ed evoluzione, 
                  che nasce dalla passione nella lavorazione del ferro da oltre 40 anni.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  L'arte da cui prendono vita i nostri prodotti è l'espressione del vero 
                  artigianato italiano, in cui si sposano l'eccellenza nella lavorazione 
                  del ferro e la passione che ne ispira le forme.
                </p>
              </div>
              <div className="w-full md:w-1/2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-[#406580]/10 rounded-xl transform rotate-3"></div>
                  <img
                    src="https://www.bsdesign.it/wp-content/uploads/2021/12/1.jpg"
                    alt="About Us"
                    className="relative rounded-lg shadow-xl"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-[#406580] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl"
              >
                <h3 className="text-3xl font-bold text-white">40+</h3>
                <p className="text-white/80 mt-2">Anni di esperienza</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl"
              >
                <h3 className="text-3xl font-bold text-white">200+</h3>
                <p className="text-white/80 mt-2">Clienti soddisfatti</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl"
              >
                <h3 className="text-3xl font-bold text-white">1000+</h3>
                <p className="text-white/80 mt-2">Progetti completati</p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Mission & Vision Section */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div 
              whileHover={{ y: -5 }}
              className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100"
            >
              <RiLightbulbLine className="text-4xl text-[#406580] mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">La Nostra Missione</h2>
              <p className="text-gray-600">
                Siamo impegnati a creare capolavori in ferro che uniscono tradizione, 
                innovazione e design moderno. La nostra missione è dare vita a pezzi 
                unici che ispirino i nostri clienti.
              </p>
            </motion.div>
            <motion.div 
              whileHover={{ y: -5 }}
              className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100"
            >
              <RiPlantLine className="text-4xl text-[#406580] mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">La Nostra Visione</h2>
              <p className="text-gray-600">
                Guardiamo al futuro con l'obiettivo di essere leader nell'artigianato 
                del ferro, sposando tecnologia avanzata e un approccio artistico.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Values Section */}
        <div className="bg-gray-50 py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900">I Nostri Valori</h2>
              <p className="mt-4 text-lg text-gray-600">
                Principi che guidano il nostro lavoro quotidiano
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ValueCard 
                icon={<FaHammer />}
                title="Innovazione"
                description="Unire tradizione e tecnologia per creare prodotti unici."
              />
              <ValueCard 
                icon={<FaStar />}
                title="Eccellenza"
                description="Garantiamo qualità superiore in ogni dettaglio."
              />
              <ValueCard 
                icon={<RiHeartLine />}
                title="Passione"
                description="Ogni progetto è ispirato dalla nostra dedizione all'arte."
              />
            </div>
          </div>
        </div>
      </Layout>
    </section>
  );
}

const ValueCard = ({ icon, title, description }) => {
  return (
    <motion.div 
      whileHover={{ y: -5 }}
      className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100"
    >
      <div className="w-12 h-12 bg-[#406580]/10 rounded-xl flex items-center justify-center text-[#406580] text-2xl mb-6">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </motion.div>
  );
};